import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2,
  LogOut,
  Lock,
  CalendarDays,
  MessageSquare,
  Inbox,
  Clock,
  CheckCircle2,
  XCircle,
  CalendarOff,
  Plus,
  Trash2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cqvet_admin_token";

function StatusBadge({ status }) {
  const map = {
    pending: { label: "Pendiente", className: "bg-amber-100 text-amber-800" },
    confirmed: {
      label: "Confirmada",
      className: "bg-emerald-100 text-emerald-800",
    },
    completed: { label: "Completada", className: "bg-slate-200 text-slate-800" },
    cancelled: { label: "Cancelada", className: "bg-rose-100 text-rose-800" },
  };
  const v = map[status] || map.pending;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${v.className}`}
    >
      {v.label}
    </span>
  );
}

function LoginCard({ onAuth }) {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    try {
      setLoading(true);
      const res = await axios.post(`${API}/admin/login`, { password: pwd });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onAuth(res.data.token);
      toast.success("Bienvenido");
    } catch (err) {
      toast.error("Contraseña incorrecta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="grid min-h-screen place-items-center bg-[#F8FAFC] px-6"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
        <Logo />
        <h1 className="mt-6 font-display text-2xl font-medium tracking-tight text-slate-900">
          Acceso administrativo
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Ingresa la contraseña para ver reservas y mensajes.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="admin-pwd">Contraseña</Label>
            <Input
              id="admin-pwd"
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="••••••••"
              className="mt-1.5 rounded-xl"
              data-testid="admin-password-input"
              autoFocus
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full rounded-full bg-[#1E3A8A] text-white hover:bg-[#172554]"
          >
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Lock className="mr-2 h-4 w-4" />
            )}
            Ingresar
          </Button>
        </form>

        <Link
          to="/"
          className="mt-6 block text-center text-xs text-slate-500 hover:text-slate-800"
        >
          ← Volver al sitio
        </Link>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div
      className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5"
      data-testid={`admin-stat-${label.toLowerCase()}`}
    >
      <span
        className={`grid h-12 w-12 place-items-center rounded-xl ${color}`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <div className="text-2xl font-display font-medium text-slate-900">
          {value}
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-500">
          {label}
        </div>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [stats, setStats] = useState({
    contacts: 0,
    bookings: 0,
    pending_bookings: 0,
    holidays: 0,
  });
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newHoliday, setNewHoliday] = useState({ date: "", reason: "" });
  const [savingHoliday, setSavingHoliday] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      setLoading(true);
      const [s, b, c, h] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/holidays`),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setContacts(c.data);
      setHolidays(h.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Sesión expirada");
        onLogout();
      } else {
        toast.error("Error al cargar datos");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/bookings/${id}/status?status=${status}`,
        {},
        { headers }
      );
      toast.success("Estado actualizado");
      load();
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  const addHoliday = async (e) => {
    e.preventDefault();
    if (!newHoliday.date || !newHoliday.reason) {
      toast.error("Completa fecha y motivo");
      return;
    }
    try {
      setSavingHoliday(true);
      await axios.post(`${API}/holidays`, newHoliday, { headers });
      toast.success("Feriado agregado");
      setNewHoliday({ date: "", reason: "" });
      load();
    } catch {
      toast.error("No se pudo agregar el feriado");
    } finally {
      setSavingHoliday(false);
    }
  };

  const removeHoliday = async (date) => {
    if (!window.confirm(`¿Eliminar feriado ${date}?`)) return;
    try {
      await axios.delete(`${API}/holidays/${date}`, { headers });
      toast.success("Feriado eliminado");
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="hidden text-xs uppercase tracking-[0.18em] text-slate-500 md:inline">
              Panel administrativo
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/">
              <Button variant="outline" className="rounded-full">
                Ver sitio
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="rounded-full"
            >
              <LogOut className="mr-2 h-4 w-4" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
        <h1 className="font-display text-3xl font-medium tracking-tight text-slate-900">
          Resumen
        </h1>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat
            icon={CalendarDays}
            label="Reservas"
            value={stats.bookings}
            color="bg-[#1E3A8A]/8 text-[#1E3A8A]"
          />
          <Stat
            icon={Clock}
            label="Pendientes"
            value={stats.pending_bookings}
            color="bg-amber-500/10 text-amber-600"
          />
          <Stat
            icon={Inbox}
            label="Mensajes"
            value={stats.contacts}
            color="bg-[#EA580C]/10 text-[#EA580C]"
          />
          <Stat
            icon={CalendarOff}
            label="Feriados"
            value={stats.holidays || 0}
            color="bg-rose-500/10 text-rose-600"
          />
        </div>

        <Tabs defaultValue="bookings" className="mt-10">
          <TabsList className="rounded-full bg-white border border-slate-200 p-1">
            <TabsTrigger
              value="bookings"
              data-testid="admin-tab-bookings"
              className="rounded-full data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white"
            >
              <CalendarDays className="mr-2 h-4 w-4" /> Reservas
            </TabsTrigger>
            <TabsTrigger
              value="contacts"
              data-testid="admin-tab-contacts"
              className="rounded-full data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white"
            >
              <MessageSquare className="mr-2 h-4 w-4" /> Mensajes
            </TabsTrigger>
            <TabsTrigger
              value="holidays"
              data-testid="admin-tab-holidays"
              className="rounded-full data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white"
            >
              <CalendarOff className="mr-2 h-4 w-4" /> Feriados
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-5">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha / Hora</TableHead>
                    <TableHead>Mascota</TableHead>
                    <TableHead>Servicio</TableHead>
                    <TableHead>Tutor</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="py-12 text-center">
                        <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ) : bookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="py-12 text-center text-sm text-slate-500">
                        Sin reservas todavía.
                      </TableCell>
                    </TableRow>
                  ) : (
                    bookings.map((b) => (
                      <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                        <TableCell>
                          <div className="font-medium text-slate-900">
                            {b.date}
                          </div>
                          <div className="text-xs text-slate-500">{b.time}</div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-slate-900">
                            {b.pet_name}
                          </div>
                          <div className="text-xs text-slate-500">
                            {b.pet_species}
                          </div>
                        </TableCell>
                        <TableCell>{b.service}</TableCell>
                        <TableCell>{b.owner_name}</TableCell>
                        <TableCell>
                          <div className="text-sm">{b.phone}</div>
                          <div className="text-xs text-slate-500">{b.email}</div>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={b.status} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {b.status !== "confirmed" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-full"
                                onClick={() => updateStatus(b.id, "confirmed")}
                                data-testid={`booking-confirm-${b.id}`}
                              >
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              </Button>
                            )}
                            {b.status !== "cancelled" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-full"
                                onClick={() => updateStatus(b.id, "cancelled")}
                                data-testid={`booking-cancel-${b.id}`}
                              >
                                <XCircle className="h-4 w-4 text-rose-600" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="mt-5">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead>Mensaje</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-12 text-center">
                        <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ) : contacts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-12 text-center text-sm text-slate-500">
                        Sin mensajes todavía.
                      </TableCell>
                    </TableRow>
                  ) : (
                    contacts.map((c) => (
                      <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                        <TableCell className="text-sm text-slate-600">
                          {new Date(c.created_at).toLocaleString("es-CL")}
                        </TableCell>
                        <TableCell className="font-medium">{c.name}</TableCell>
                        <TableCell>
                          <div className="text-sm">{c.email}</div>
                          {c.phone && (
                            <div className="text-xs text-slate-500">
                              {c.phone}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="max-w-md text-sm text-slate-700">
                          {c.message}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="holidays" className="mt-5">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Add holiday form */}
              <form
                onSubmit={addHoliday}
                data-testid="admin-holiday-form"
                className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-1"
              >
                <h3 className="font-display text-lg font-medium text-slate-900">
                  Agregar feriado
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  Los feriados bloquean la reserva de ese día.
                </p>
                <div className="mt-4 space-y-3">
                  <div>
                    <Label htmlFor="h-date">Fecha</Label>
                    <Input
                      id="h-date"
                      type="date"
                      value={newHoliday.date}
                      onChange={(e) =>
                        setNewHoliday((p) => ({ ...p, date: e.target.value }))
                      }
                      className="mt-1.5 rounded-xl"
                      data-testid="admin-holiday-date-input"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="h-reason">Motivo</Label>
                    <Input
                      id="h-reason"
                      value={newHoliday.reason}
                      onChange={(e) =>
                        setNewHoliday((p) => ({
                          ...p,
                          reason: e.target.value,
                        }))
                      }
                      placeholder="Ej: Cierre por capacitación"
                      className="mt-1.5 rounded-xl"
                      data-testid="admin-holiday-reason-input"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={savingHoliday}
                    data-testid="admin-holiday-submit-btn"
                    className="w-full rounded-full bg-[#1E3A8A] text-white hover:bg-[#172554]"
                  >
                    {savingHoliday ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="mr-2 h-4 w-4" />
                    )}
                    Agregar feriado
                  </Button>
                </div>
              </form>

              {/* Holidays list */}
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white lg:col-span-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Motivo</TableHead>
                      <TableHead>Origen</TableHead>
                      <TableHead className="text-right">Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={4} className="py-12 text-center">
                          <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />
                        </TableCell>
                      </TableRow>
                    ) : holidays.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="py-12 text-center text-sm text-slate-500"
                        >
                          Sin feriados configurados.
                        </TableCell>
                      </TableRow>
                    ) : (
                      holidays.map((h) => (
                        <TableRow
                          key={h.date}
                          data-testid={`holiday-row-${h.date}`}
                        >
                          <TableCell className="font-medium text-slate-900">
                            {h.date}
                          </TableCell>
                          <TableCell>{h.reason}</TableCell>
                          <TableCell>
                            <span
                              className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                h.source === "seed"
                                  ? "bg-slate-100 text-slate-700"
                                  : "bg-[#EA580C]/10 text-[#EA580C]"
                              }`}
                            >
                              {h.source === "seed" ? "Chile 2026" : "Personal"}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full"
                              onClick={() => removeHoliday(h.date)}
                              data-testid={`holiday-delete-${h.date}`}
                            >
                              <Trash2 className="h-4 w-4 text-rose-600" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(
    typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null
  );

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginCard onAuth={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
